import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Button, CircularProgress, IconButton, Menu, MenuItem, Slider, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import Forward10Icon from '@mui/icons-material/Forward10';
import PauseIcon from '@mui/icons-material/Pause';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Replay10Icon from '@mui/icons-material/Replay10';
import {
  channelProfileImagePath,
  downloadLessonFile,
  fetchAudioBlobUrl,
  fetchLibraryChannels,
  fetchLibraryLessons,
  lessonStreamPath,
} from '../mishtameretPortalApi';
import { LESSON_CHANNELS } from '../../../data/lessonChannels';
import { formatHebrewDate } from '../../../utils/hebrewDate';

/** Sticky header (logo block + nav) — keep now-playing bar below it */
const STICKY_PLAYER_TOP = { xs: 132, sm: 140 };

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];

function formatDate(iso) {
  return formatHebrewDate(iso);
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function finiteDuration(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

const LibraryScreen = () => {
  const { theme } = useOutletContext();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeLesson, setActiveLesson] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [playbackRate, setPlaybackRate] = useState(1);
  const [rateOpen, setRateOpen] = useState(false);
  const audioRef = useRef(null);
  const blobRef = useRef('');
  const profileBlobRef = useRef('');
  const seekingRef = useRef(false);
  const activeLessonRef = useRef(null);
  const playPromiseRef = useRef(null);
  const playbackRateRef = useRef(1);
  const rateBtnRef = useRef(null);
  /** User intent — prevents stray play() / onplay from fighting pause */
  const wantPausedRef = useRef(false);

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    playPromiseRef.current = null;
    wantPausedRef.current = false;
    if (audio) {
      audio.onended = null;
      audio.ontimeupdate = null;
      audio.onloadedmetadata = null;
      audio.ondurationchange = null;
      audio.onloadeddata = null;
      audio.onplay = null;
      audio.onpause = null;
      audio.onerror = null;
      try { audio.pause(); } catch { /* ignore */ }
      audio.removeAttribute('src');
      audio.load();
      audioRef.current = null;
    }
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = '';
    }
    setActiveLesson(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPaused(false);
    setRateOpen(false);
  }, []);

  useEffect(() => {
    activeLessonRef.current = activeLesson;
  }, [activeLesson]);

  useEffect(() => {
    stopPlayback();
  }, [channelId, stopPlayback]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (!channelId) {
          if (profileBlobRef.current) {
            URL.revokeObjectURL(profileBlobRef.current);
            profileBlobRef.current = '';
          }
          if (!cancelled) {
            setProfileImageUrl('');
            const data = await fetchLibraryChannels();
            if (!cancelled) setChannels(data.channels || []);
          }
        } else {
          const data = await fetchLibraryLessons(channelId);
          if (cancelled) return;
          setLessons(data.lessons || []);

          if (profileBlobRef.current) {
            URL.revokeObjectURL(profileBlobRef.current);
            profileBlobRef.current = '';
          }
          setProfileImageUrl('');
          if (data.hasProfileImage && data.profileImagePath) {
            try {
              const url = await fetchAudioBlobUrl(
                data.profileImagePath || channelProfileImagePath(channelId)
              );
              if (cancelled) {
                URL.revokeObjectURL(url);
                return;
              }
              profileBlobRef.current = url;
              setProfileImageUrl(url);
            } catch {
              /* no profile image */
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת הספרייה');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [channelId]);

  useEffect(() => () => {
    stopPlayback();
    if (profileBlobRef.current) URL.revokeObjectURL(profileBlobRef.current);
  }, [stopPlayback]);

  const syncDurationFromAudio = useCallback((audio, fallback = 0) => {
    const fromMedia = finiteDuration(audio.duration);
    if (fromMedia) {
      setDuration(fromMedia);
      return fromMedia;
    }
    const fromMeta = finiteDuration(fallback);
    if (fromMeta) setDuration((prev) => (prev > 0 ? prev : fromMeta));
    return fromMeta;
  }, []);

  const pauseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Pause immediately — never await play(); a pending play() promise can hang
    // and then audio.pause() never runs while sound keeps going.
    wantPausedRef.current = true;
    setIsPaused(true);
    playPromiseRef.current = null;
    try {
      audio.pause();
    } catch {
      /* ignore */
    }
  }, []);

  const resumeAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    wantPausedRef.current = false;
    setIsPaused(false);
    try {
      const pending = audio.play();
      playPromiseRef.current = pending;
      await pending;
      if (wantPausedRef.current && audioRef.current === audio) {
        audio.pause();
        setIsPaused(true);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      wantPausedRef.current = true;
      setIsPaused(true);
      setError('לא ניתן להמשיך את ההשמעה כרגע');
    } finally {
      playPromiseRef.current = null;
    }
  }, []);

  const togglePauseResume = useCallback((event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    const audio = audioRef.current;
    if (!audio || !activeLessonRef.current) return;
    // Prefer user-intent + actual playing state over audio.paused alone
    const shouldPause = !wantPausedRef.current && !audio.paused;
    if (shouldPause) {
      pauseAudio();
    } else {
      void resumeAudio();
    }
  }, [pauseAudio, resumeAudio]);

  const bindAudioEvents = useCallback((audio, lesson) => {
    const fallback = lesson?.duration || 0;
    audio.ontimeupdate = () => {
      if (!seekingRef.current) setCurrentTime(audio.currentTime || 0);
    };
    audio.onloadedmetadata = () => syncDurationFromAudio(audio, fallback);
    audio.ondurationchange = () => syncDurationFromAudio(audio, fallback);
    audio.onloadeddata = () => syncDurationFromAudio(audio, fallback);
    audio.onplay = () => {
      if (wantPausedRef.current) {
        audio.pause();
        return;
      }
      setIsPaused(false);
    };
    audio.onpause = () => {
      if (audioRef.current === audio && !audio.ended) setIsPaused(true);
    };
    audio.onended = () => {
      stopPlayback();
    };
    audio.onerror = () => {
      setError('שגיאה בניגון השיעור');
      stopPlayback();
    };
  }, [stopPlayback, syncDurationFromAudio]);

  const playLesson = async (lesson) => {
    if (activeLesson?.id === lesson.id && audioRef.current) {
      togglePauseResume();
      return;
    }

    stopPlayback();
    setLoadingAudioId(lesson.id);
    setError('');
    wantPausedRef.current = false;
    try {
      const url = await fetchAudioBlobUrl(lessonStreamPath(lesson.id));
      if (wantPausedRef.current) {
        setLoadingAudioId(null);
        return;
      }
      blobRef.current = url;
      setActiveLesson(lesson);
      setIsPaused(false);
      setCurrentTime(0);
      setDuration(finiteDuration(lesson.duration));
      const audio = new Audio();
      audio.preload = 'auto';
      audio.playbackRate = playbackRateRef.current;
      audio.defaultPlaybackRate = playbackRateRef.current;
      audioRef.current = audio;
      bindAudioEvents(audio, lesson);
      audio.src = url;
      const pending = audio.play();
      playPromiseRef.current = pending;
      try {
        await pending;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        throw err;
      }
      if (wantPausedRef.current && audioRef.current === audio) {
        audio.pause();
        setIsPaused(true);
      }
    } catch {
      setError('לא ניתן להשמיע את השיעור כרגע');
      stopPlayback();
    } finally {
      playPromiseRef.current = null;
      setLoadingAudioId(null);
    }
  };

  const seekBy = (delta) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = finiteDuration(audio.duration) || duration || 0;
    const clamped = max > 0
      ? Math.max(0, Math.min(max, audio.currentTime + delta))
      : Math.max(0, audio.currentTime + delta);
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const changePlaybackRate = (rate) => {
    const next = Number(rate);
    if (!PLAYBACK_RATES.includes(next)) return;
    playbackRateRef.current = next;
    setPlaybackRate(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
      audioRef.current.defaultPlaybackRate = next;
    }
  };

  const handleSeek = (_event, value) => {
    seekingRef.current = true;
    setCurrentTime(value);
  };

  const handleSeekCommitted = (_event, value) => {
    seekingRef.current = false;
    if (audioRef.current) {
      audioRef.current.currentTime = value;
    }
    setCurrentTime(value);
  };

  const handleDownload = async (lesson) => {
    setDownloadingId(lesson.id);
    try {
      await downloadLessonFile(lesson.id, `${lesson.title || 'shiur'}.wav`);
    } catch {
      setError('ההורדה נכשלה');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  if (!channelId) {
    return (
      <Box>
        {error ? <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography> : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {channels.map((ch) => (
            <Box
              key={ch.id}
              component="button"
              type="button"
              onClick={() => navigate(`/mishtameret/library/${ch.id}`)}
              sx={{
                textAlign: 'right',
                p: 2,
                minHeight: 64,
                borderRadius: `${theme.radius}px`,
                border: `1px solid ${theme.borderSoft}`,
                bgcolor: theme.surface,
                color: theme.text,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                '&:hover': { borderColor: theme.accent, bgcolor: theme.surfaceHover },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{ch.label}</Typography>
                <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>
                  {ch.lessonCount} שיעורים
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: theme.accent, transform: 'scaleX(-1)' }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const channelLabel =
    channels.find((c) => c.id === channelId)?.label ||
    LESSON_CHANNELS.find((c) => c.id === channelId)?.label ||
    'שיעורים';

  const playingId = activeLesson?.id || null;
  const sliderMax = duration > 0 ? duration : Math.max(currentTime, 1);

  const renderProgress = (key) => (
    <Box key={key} sx={{ width: '100%', px: 0.25 }} onClick={(e) => e.stopPropagation()}>
      <Slider
        size="small"
        value={Math.min(currentTime, sliderMax)}
        min={0}
        max={sliderMax}
        step={0.1}
        onChange={handleSeek}
        onChangeCommitted={handleSeekCommitted}
        aria-label="התקדמות השיעור"
        sx={{
          color: theme.accent,
          py: 0.75,
          pointerEvents: 'auto',
          '& .MuiSlider-thumb': { width: 16, height: 16 },
          '& .MuiSlider-rail': { opacity: 0.35 },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          color: theme.textMuted,
          fontSize: '0.75rem',
          fontVariantNumeric: 'tabular-nums',
          mt: -0.75,
        }}
      >
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </Box>
    </Box>
  );

  const controlBtnSx = {
    width: 44,
    height: 44,
    color: theme.accent,
    border: `1px solid ${theme.borderSoft}`,
    flexShrink: 0,
    pointerEvents: 'auto',
  };

  const primaryBtnSx = {
    width: 48,
    height: 48,
    color: theme.accentContrast,
    bgcolor: theme.accent,
    flexShrink: 0,
    pointerEvents: 'auto',
    '&:hover': { bgcolor: theme.accentSoft },
    '&.Mui-disabled': { bgcolor: theme.borderSoft },
  };

  return (
    <Box>
      <Button
        onClick={() => {
          stopPlayback();
          navigate('/mishtameret/library');
        }}
        startIcon={<ArrowForwardIcon sx={{ transform: 'none' }} />}
        sx={{
          color: theme.accent,
          mb: 1.5,
          fontWeight: 600,
          minHeight: 40,
          '& .MuiButton-startIcon': { marginInlineEnd: 1.25 },
        }}
      >
        לכל הערוצים
      </Button>

      {error ? <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography> : null}

      {activeLesson ? (
        <Box
          sx={{
            position: 'sticky',
            top: STICKY_PLAYER_TOP,
            zIndex: 9,
            mb: 2,
            p: 1.75,
            borderRadius: `${theme.radius}px`,
            border: `1px solid ${theme.border}`,
            bgcolor: theme.navBg,
            boxShadow: theme.shadow,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: theme.accent, fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.3 }}>
                מושמע כעת
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }} noWrap>
                {activeLesson.title}
              </Typography>
              {activeLesson.speaker ? (
                <Typography sx={{ color: theme.textMuted, fontSize: '0.8rem' }} noWrap>
                  {activeLesson.speaker}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              type="button"
              onClick={() => handleDownload(activeLesson)}
              disabled={downloadingId === activeLesson.id}
              aria-label="הורדה"
              sx={controlBtnSx}
            >
              {downloadingId === activeLesson.id ? (
                <CircularProgress size={20} sx={{ color: theme.accent }} />
              ) : (
                <DownloadIcon />
              )}
            </IconButton>
          </Box>

          {renderProgress('banner-progress')}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <IconButton
              type="button"
              onClick={() => seekBy(-10)}
              aria-label="אחורה 10 שניות"
              sx={controlBtnSx}
            >
              <Replay10Icon />
            </IconButton>
            <IconButton
              type="button"
              onClick={togglePauseResume}
              aria-label={isPaused ? 'המשך השמעה' : 'השהיה'}
              sx={primaryBtnSx}
            >
              {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
            <IconButton
              type="button"
              onClick={() => seekBy(10)}
              aria-label="קדימה 10 שניות"
              sx={controlBtnSx}
            >
              <Forward10Icon />
            </IconButton>
            <Button
              ref={rateBtnRef}
              type="button"
              onClick={() => setRateOpen((open) => !open)}
              aria-expanded={rateOpen}
              aria-controls="playback-rate-menu"
              aria-haspopup="menu"
              sx={{
                minWidth: 64,
                minHeight: 44,
                px: 1.5,
                color: rateOpen || playbackRate !== 1 ? theme.accentContrast : theme.accent,
                bgcolor: rateOpen || playbackRate !== 1 ? theme.accent : 'transparent',
                border: `1px solid ${theme.borderSoft}`,
                borderRadius: `${theme.radius}px`,
                fontWeight: 700,
                fontSize: '0.85rem',
                '&:hover': {
                  bgcolor: rateOpen || playbackRate !== 1 ? theme.accentSoft : theme.surfaceHover,
                  borderColor: theme.accent,
                },
              }}
            >
              קצב{playbackRate !== 1 ? ` ${playbackRate}x` : ''}
            </Button>
          </Box>

          <Menu
            id="playback-rate-menu"
            anchorEl={rateBtnRef.current}
            open={rateOpen}
            onClose={() => setRateOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            PaperProps={{
              sx: {
                mt: -0.75,
                minWidth: 132,
                borderRadius: `${theme.radius}px`,
                border: `1px solid ${theme.borderSoft}`,
                bgcolor: theme.navBg,
                boxShadow: theme.shadow,
                py: 0.5,
              },
            }}
            MenuListProps={{
              dense: true,
              'aria-label': 'בחירת קצב השמעה',
              sx: { py: 0.25 },
            }}
          >
            {[...PLAYBACK_RATES].reverse().map((rate) => {
              const selected = playbackRate === rate;
              return (
                <MenuItem
                  key={rate}
                  selected={selected}
                  onClick={() => {
                    changePlaybackRate(rate);
                    setRateOpen(false);
                  }}
                  sx={{
                    minHeight: 40,
                    px: 1.5,
                    gap: 1,
                    fontWeight: selected ? 700 : 500,
                    fontSize: '0.9rem',
                    color: theme.text,
                    '&.Mui-selected': {
                      bgcolor: 'transparent',
                      color: theme.accent,
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: theme.surfaceHover,
                    },
                    '&:hover': {
                      bgcolor: theme.surfaceHover,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.accent,
                    }}
                  >
                    {selected ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
                  </Box>
                  {rate === 1 ? 'רגיל' : `${rate}`}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      ) : null}

      {!lessons.length ? (
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.radius}px`,
            border: `1px dashed ${theme.border}`,
            color: theme.textMuted,
            textAlign: 'center',
          }}
        >
          אין שיעורים בערוץ זה
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {lessons.map((lesson) => {
            const active = playingId === lesson.id;
            return (
              <Box
                key={lesson.id}
                sx={{
                  p: 1.75,
                  borderRadius: `${theme.radius}px`,
                  border: `1px solid ${active ? theme.accent : theme.borderSoft}`,
                  bgcolor: theme.surface,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: active ? 1.25 : 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    src={profileImageUrl || undefined}
                    alt={lesson.speaker || channelLabel}
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      bgcolor: theme.borderSoft,
                      color: theme.textMuted,
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem' }} noWrap>
                      {lesson.title}
                    </Typography>
                    <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }} noWrap>
                      {[lesson.speaker, formatDate(lesson.date)].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  <IconButton
                    type="button"
                    onClick={() => handleDownload(lesson)}
                    disabled={downloadingId === lesson.id}
                    aria-label="הורדה"
                    sx={controlBtnSx}
                  >
                    {downloadingId === lesson.id ? (
                      <CircularProgress size={20} sx={{ color: theme.accent }} />
                    ) : (
                      <DownloadIcon />
                    )}
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={() => playLesson(lesson)}
                    disabled={loadingAudioId === lesson.id}
                    aria-label={active && !isPaused ? 'השהיה' : 'השמעה'}
                    sx={primaryBtnSx}
                  >
                    {loadingAudioId === lesson.id ? (
                      <CircularProgress size={22} sx={{ color: theme.accentContrast }} />
                    ) : active && !isPaused ? (
                      <PauseIcon />
                    ) : (
                      <PlayArrowIcon />
                    )}
                  </IconButton>
                </Box>

                {active ? renderProgress(`row-progress-${lesson.id}`) : null}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default LibraryScreen;
